import { useEffect, useState } from 'react'
import api from '../services/api'
import FuncionarioFeriasVenda from './FuncionarioFeriasVenda'

// Férias do funcionário (TMOTCA8 do ERP, frota.motca8).
// Início já gravado é a chave da linha e não muda; linha não pode ser excluída (igual ao ERP).

interface Linha { ini: string; fim: string; obs: string; nova?: boolean }

const diasEntre = (ini: string, fim: string) => {
  if (!ini || !fim || fim < ini) return ''
  const d = Math.round((new Date(`${fim}T12:00:00`).getTime() - new Date(`${ini}T12:00:00`).getTime()) / 86400000) + 1
  return `${d} dia${d === 1 ? '' : 's'}`
}

export default function FuncionarioFeriasModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const [linhas, setLinhas] = useState<Linha[] | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/ferias`)
    setLinhas(r.data)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar as férias do funcionário.'))
  }, [motcod])

  const set = (i: number, campo: keyof Linha, valor: string) =>
    setLinhas(ls => ls!.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)))

  async function salvar() {
    setErro(''); setAviso('')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/ferias`, { linhas })
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Férias salvas.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar as férias.')
    } finally {
      setSalvando(false)
    }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.3rem 0.4rem' }
  const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '0.35rem 0.5rem', fontSize: '0.82rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 880, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Férias — {motcod} · {motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCA8</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!linhas ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 150 }}>Início</th>
                    <th style={{ ...th, width: 150 }}>Fim</th>
                    <th style={{ ...th, width: 70 }}>Dias</th>
                    <th style={th}>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: '#6b7280', padding: '1.2rem' }}>Nenhuma férias lançada.</td></tr>
                  )}
                  {linhas.map((l, i) => (
                    <tr key={l.nova ? `nova-${i}` : l.ini}>
                      <td style={td}>
                        <input type="date" value={l.ini} disabled={!l.nova} style={inp}
                          title={l.nova ? undefined : 'Início já gravado não pode ser alterado'}
                          onChange={e => set(i, 'ini', e.target.value)} />
                      </td>
                      <td style={td}>
                        <input type="date" value={l.fim} min={l.ini || undefined} style={inp} onChange={e => set(i, 'fim', e.target.value)} />
                      </td>
                      <td style={{ ...td, fontSize: '0.8rem', color: '#6b7280' }}>{diasEntre(l.ini, l.fim) || '—'}</td>
                      <td style={td}>
                        <input value={l.obs} maxLength={50} style={inp} onChange={e => set(i, 'obs', e.target.value.toUpperCase())} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}
                onClick={() => setLinhas(ls => [...ls!, { ini: '', fim: '', obs: '', nova: true }])}>
                + Novas férias
              </button>
            </>
          )}

          {linhas && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}

          <FuncionarioFeriasVenda motcod={motcod} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !linhas}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
